import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/axios';

const Login = () => {
  const [showRegister, setShowRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreeAll, setAgreeAll] = useState(false);
  const [agreeAge, setAgreeAge] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  // 이메일 형식 검증
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 이메일 중복 체크
  const checkEmailAvailability = async (email) => {
    if (!email) {
      setEmailError('');
      return true;
    }

    if (!validateEmail(email)) {
      setEmailError('유효한 이메일 주소를 입력하세요.');
      return false;
    }

    try {
      setCheckingEmail(true);
      setEmailError('');
      const response = await apiClient.post('/auth/check-email', { email });
      
      if (!response.data.available) {
        setEmailError(response.data.message || '이미 사용 중인 이메일입니다.');
        return false;
      }
      
      return true;
    } catch (error) {
      if (error.response?.data?.error) {
        setEmailError(error.response.data.error);
      } else {
        setEmailError('이메일 중복 체크 중 오류가 발생했습니다.');
      }
      return false;
    } finally {
      setCheckingEmail(false);
    }
  };

  // 사용자명 검증
  const validateUsername = (username) => {
    if (!username) {
      return { valid: false, error: '사용자명을 입력하세요.' };
    }
    
    if (username.toLowerCase() === 'admin') {
      return { valid: false, error: '"admin"은 사용할 수 없는 사용자명입니다.' };
    }
    
    return { valid: true, error: '' };
  };

  // 휴대폰 번호 검증
  const validatePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) {
      return { valid: false, error: '휴대폰 번호를 입력하세요.' };
    }
    
    // 숫자만 허용 (하이픈 제거 후 검증)
    const cleanedPhone = phoneNumber.replace(/[-\s]/g, '');
    const phoneRegex = /^01[0-9]{9}$/; // 010으로 시작하는 11자리 숫자
    
    if (!phoneRegex.test(cleanedPhone)) {
      return { valid: false, error: '올바른 휴대폰 번호를 입력하세요. (예: 01012345678)' };
    }
    
    return { valid: true, error: '' };
  };

  // 전체동의 핸들러
  const handleAgreeAll = (event) => {
    const checked = event.target.checked;
    setAgreeAll(checked);
    setAgreeAge(checked);
    setAgreeTerms(checked);
    setAgreePrivacy(checked);
  };

  // 개별 동의 핸들러
  const handleAgreeAge = (event) => {
    const checked = event.target.checked;
    setAgreeAge(checked);
    // 다른 동의 항목들의 현재 상태를 확인하여 전체동의 업데이트
    setAgreeAll(checked && agreeTerms && agreePrivacy);
  };

  const handleAgreeTerms = (event) => {
    const checked = event.target.checked;
    setAgreeTerms(checked);
    // 다른 동의 항목들의 현재 상태를 확인하여 전체동의 업데이트
    setAgreeAll(agreeAge && checked && agreePrivacy);
  };

  const handleAgreePrivacy = (event) => {
    const checked = event.target.checked;
    setAgreePrivacy(checked);
    // 다른 동의 항목들의 현재 상태를 확인하여 전체동의 업데이트
    setAgreeAll(agreeAge && agreeTerms && checked);
  };

  // 동의 약관 보기 핸들러
  const handleViewTerms = (e) => {
    e.preventDefault();
    const width = 800;
    const height = 900;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open(
      '/terms',
      'terms',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  };

  const handleViewPrivacy = (e) => {
    e.preventDefault();
    const width = 800;
    const height = 900;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open(
      '/privacy',
      'privacy',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setEmailError('');
    setLoading(true);

    try {
      if (!email) {
        setEmailError('이메일을 입력하세요.');
        setLoading(false);
        return;
      }

      if (!validateEmail(email)) {
        setEmailError('유효한 이메일 주소를 입력하세요.');
        setLoading(false);
        return;
      }

      if (!password) {
        setError('비밀번호를 입력하세요.');
        setLoading(false);
        return;
      }

      const result = await login(email, password);
      
      if (result.success) {
        navigate('/dashboard');
      } else {
        // 이메일 인증이 필요한 경우
        if (result.requiresVerification) {
          navigate(`/verify-email?email=${encodeURIComponent(email)}`);
        } else {
          setError(result.error);
        }
      }
    } catch (err) {
      setError('오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setEmailError('');
    setUsernameError('');
    setPhoneNumberError('');
    setPasswordError('');
    setLoading(true);

    try {
      if (!email) {
        setEmailError('이메일을 입력하세요.');
        setLoading(false);
        return;
      }

      if (!validateEmail(email)) {
        setEmailError('유효한 이메일 주소를 입력하세요.');
        setLoading(false);
        return;
      }

      const isEmailAvailable = await checkEmailAvailability(email);
      if (!isEmailAvailable) {
        setLoading(false);
        return;
      }

      const usernameValidation = validateUsername(username);
      if (!usernameValidation.valid) {
        setUsernameError(usernameValidation.error);
        setLoading(false);
        return;
      }

      const phoneNumberValidation = validatePhoneNumber(phoneNumber);
      if (!phoneNumberValidation.valid) {
        setPhoneNumberError(phoneNumberValidation.error);
        setLoading(false);
        return;
      }

      if (!password) {
        setPasswordError('비밀번호를 입력하세요.');
        setLoading(false);
        return;
      }

      if (password.length < 6) {
        setPasswordError('비밀번호는 최소 6자 이상이어야 합니다.');
        setLoading(false);
        return;
      }

      if (!confirmPassword) {
        setPasswordError('비밀번호를 다시 입력하세요.');
        setLoading(false);
        return;
      }

      if (password !== confirmPassword) {
        setPasswordError('비밀번호가 일치하지 않습니다.');
        setLoading(false);
        return;
      }

      // 필수 동의 항목 확인
      if (!agreeAge) {
        setError('만 14세 이상 동의는 필수입니다.');
        setLoading(false);
        return;
      }

      if (!agreeTerms) {
        setError('이용약관 동의는 필수입니다.');
        setLoading(false);
        return;
      }

      if (!agreePrivacy) {
        setError('개인정보 수집 및 이용 동의는 필수입니다.');
        setLoading(false);
        return;
      }

      // 휴대폰 번호에서 하이픈 제거
      const cleanedPhoneNumber = phoneNumber.replace(/[-\s]/g, '');

      const result = await register(email, username, cleanedPhoneNumber, password);
      
      if (result.success) {
        if (result.requiresVerification) {
          // 이메일 인증 페이지로 이동
          navigate(`/verify-email?email=${encodeURIComponent(result.email || email)}`);
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper 
          elevation={0}
          sx={{ 
            padding: 4, 
            width: '115%',
            maxWidth: '575px',
            margin: '0 auto',
            bgcolor: 'background.default',
            border: 'none',
            boxShadow: 'none',
          }}
        >
          {!showRegister ? (
            // 로그인 화면
            <>
              <Link
                href={`${process.env.PUBLIC_URL || ''}/plango-landing.html`}
                variant="body2"
                underline="none"
                sx={{
                  color: '#D4AF37',
                  fontWeight: 600,
                  marginBottom: 1.5,
                  display: 'inline-block',
                  '&:hover': {
                    color: '#B8962E',
                    textDecoration: 'none',
                  },
                }}
              >
                ✨ PlanGo 둘러보기
              </Link>
              <Typography
                component="h1"
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  color: 'text.primary',
                  marginBottom: 0.75,
                }}
              >
                로그인
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleLogin}>
                <TextField
                  fullWidth
                  id="email"
                  placeholder="사용자이름"
                  name="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError('');
                  }}
                  error={!!emailError}
                  helperText={emailError}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                    },
                  }}
                />

                <TextField
                  fullWidth
                  name="password"
                  placeholder="비밀번호"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                    },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={loading || !!emailError}
                  sx={{
                    mt: 2,
                    mb: 0.5,
                    py: 1.5,
                    borderRadius: '4px',
                  }}
                >
                  {loading ? '처리 중...' : '로그인 진행'}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => setShowRegister(true)}
                    sx={{
                      color: 'text.primary',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    계정 만들기
                  </Link>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => {
                      alert('비밀번호 찾기 기능은 준비 중입니다.');
                    }}
                    sx={{
                      color: 'text.primary',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    비밀번호를 잊어버리셨나요?
                  </Link>
                </Box>
              </Box>
            </>
          ) : (
            // 회원가입 화면
            <>
              <Typography 
                component="h1" 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold',
                  color: 'text.primary',
                  marginBottom: 4,
                }}
              >
                회원가입
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleRegister}>
                <TextField
                  fullWidth
                  id="email"
                  label="이메일"
                  name="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={async (e) => {
                    const value = e.target.value;
                    setEmail(value);
                    if (value && !validateEmail(value)) {
                      setEmailError('유효한 이메일 주소를 입력하세요.');
                      return;
                    }
                    if (value) {
                      setTimeout(() => {
                        checkEmailAvailability(value);
                      }, 500);
                    } else {
                      setEmailError('');
                    }
                  }}
                  error={!!emailError}
                  helperText={emailError || (checkingEmail ? '이메일 확인 중...' : '')}
                  disabled={checkingEmail}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                    },
                  }}
                />

                <TextField
                  fullWidth
                  id="username"
                  label="사용자명"
                  name="username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => {
                    const value = e.target.value;
                    setUsername(value);
                    const validation = validateUsername(value);
                    setUsernameError(validation.error);
                  }}
                  error={!!usernameError}
                  helperText={usernameError || ''}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                    },
                  }}
                />

                <TextField
                  fullWidth
                  id="phoneNumber"
                  label="휴대폰 번호"
                  name="phoneNumber"
                  placeholder="01012345678"
                  value={phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9-]/g, ''); // 숫자와 하이픈만 허용
                    setPhoneNumber(value);
                    if (value) {
                      const validation = validatePhoneNumber(value);
                      setPhoneNumberError(validation.error);
                    } else {
                      setPhoneNumberError('');
                    }
                  }}
                  error={!!phoneNumberError}
                  helperText={phoneNumberError || '하이픈(-) 없이 입력하거나 포함하여 입력하세요.'}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                    },
                  }}
                />

                <TextField
                  fullWidth
                  name="password"
                  label="비밀번호"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (confirmPassword && e.target.value !== confirmPassword) {
                      setPasswordError('비밀번호가 일치하지 않습니다.');
                    } else if (confirmPassword && e.target.value === confirmPassword) {
                      setPasswordError('');
                    }
                  }}
                  error={!!passwordError}
                  helperText={passwordError}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                    },
                  }}
                />

                <TextField
                  fullWidth
                  name="confirmPassword"
                  label="비밀번호 재입력"
                  type="password"
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (e.target.value !== password) {
                      setPasswordError('비밀번호가 일치하지 않습니다.');
                    } else {
                      setPasswordError('');
                    }
                  }}
                  error={!!passwordError}
                  helperText={passwordError}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                    },
                  }}
                />

                {/* 사용자 동의 섹션 */}
                <Box sx={{ mb: 0.5, mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={agreeAll}
                        onChange={handleAgreeAll}
                        sx={{
                          color: 'text.primary',
                          '&.Mui-checked': {
                            color: 'text.primary',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                        전체동의
                      </Typography>
                    }
                  />
                </Box>

                <Box sx={{ mb: 0, ml: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={agreeAge}
                        onChange={handleAgreeAge}
                        sx={{
                          color: 'text.primary',
                          '&.Mui-checked': {
                            color: 'text.primary',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '0.875rem' }}>
                        <span style={{ color: '#d32f2f' }}>(필수)</span> 만 14세 이상입니다
                      </Typography>
                    }
                  />
                </Box>

                <Box sx={{ mb: 0, ml: 1, display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Checkbox
                    checked={agreeTerms}
                    onChange={handleAgreeTerms}
                    sx={{
                      color: 'text.primary',
                      padding: '4px',
                      '&.Mui-checked': {
                        color: 'text.primary',
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, ml: 0.5 }}>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      <span style={{ color: '#d32f2f' }}>(필수)</span> 이용약관에 동의합니다
                    </Typography>
                    <Link
                      component="button"
                      type="button"
                      onClick={handleViewTerms}
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      보기
                      <span style={{ fontSize: '0.75rem' }}>↗</span>
                    </Link>
                  </Box>
                </Box>

                <Box sx={{ mb: 0.5, ml: 1, display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Checkbox
                    checked={agreePrivacy}
                    onChange={handleAgreePrivacy}
                    sx={{
                      color: 'text.primary',
                      padding: '4px',
                      '&.Mui-checked': {
                        color: 'text.primary',
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, ml: 0.5 }}>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      <span style={{ color: '#d32f2f' }}>(필수)</span> 개인정보 수집 및 이용에 동의합니다
                    </Typography>
                    <Link
                      component="button"
                      type="button"
                      onClick={handleViewPrivacy}
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      보기
                      <span style={{ fontSize: '0.75rem' }}>↗</span>
                    </Link>
                  </Box>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={
                    loading || 
                    checkingEmail || 
                    !!emailError || 
                    !!usernameError || 
                    !!phoneNumberError ||
                    !!passwordError ||
                    !agreeAge ||
                    !agreeTerms ||
                    !agreePrivacy
                  }
                  color="primary"
                  sx={{
                    mt: 2,
                    mb: 3,
                    py: 1.5,
                    borderRadius: '4px',
                  }}
                >
                  {loading ? '처리 중...' : '회원가입'}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => {
                      setShowRegister(false);
                      setError('');
                      setEmailError('');
                      setUsernameError('');
                      setPhoneNumberError('');
                      setPasswordError('');
                      setEmail('');
                      setUsername('');
                      setPhoneNumber('');
                      setPassword('');
                      setConfirmPassword('');
                      setAgreeAll(false);
                      setAgreeAge(false);
                      setAgreeTerms(false);
                      setAgreePrivacy(false);
                    }}
                    sx={{
                      color: 'text.primary',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    이미 계정이 있으신가요? 로그인
                  </Link>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
